const DB_NAME = 'miosegno-local-v1'
const DB_VERSION = 3
const AUDIO_STORE = 'audio'
const AUDIO_META_STORE = 'audioMeta'
const PDF_STORE = 'pdf'
const PDF_META_STORE = 'pdfMeta'
const PDF_BOOKMARK_STORE = 'pdfBookmarks'

type StoreName =
  | typeof AUDIO_STORE
  | typeof AUDIO_META_STORE
  | typeof PDF_STORE
  | typeof PDF_META_STORE
  | typeof PDF_BOOKMARK_STORE

export interface PdfMetadata {
  fingerprint: string
  filename?: string
}

export type PdfBookmarkPages = Record<string, number>

export interface SharedAudioCacheMetadata {
  cacheKey: string
  shareId: string
  accessExpiresAt: string
  storedAt: string
  scope: 'SHARED_LINK'
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      for (const storeName of [AUDIO_STORE, AUDIO_META_STORE, PDF_STORE, PDF_META_STORE, PDF_BOOKMARK_STORE]) {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName)
        }
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function putValue<T>(storeName: StoreName, key: string, value: T): Promise<void> {
  const db = await openDatabase()
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    transaction.objectStore(storeName).put(value, key)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
  db.close()
}

async function getValue<T>(storeName: StoreName, key: string): Promise<T | null> {
  const db = await openDatabase()
  const value = await new Promise<T | null>((resolve, reject) => {
    const request = db.transaction(storeName, 'readonly')
      .objectStore(storeName)
      .get(key)
    request.onsuccess = () => resolve((request.result as T | undefined) ?? null)
    request.onerror = () => reject(request.error)
  })
  db.close()
  return value
}

async function getAllValues<T>(storeName: StoreName): Promise<T[]> {
  const db = await openDatabase()
  const values = await new Promise<T[]>((resolve, reject) => {
    const request = db.transaction(storeName, 'readonly').objectStore(storeName).getAll()
    request.onsuccess = () => resolve((request.result as T[] | undefined) ?? [])
    request.onerror = () => reject(request.error)
  })
  db.close()
  return values
}

async function remove(storeName: StoreName, key: string): Promise<void> {
  const db = await openDatabase()
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite')
    transaction.objectStore(storeName).delete(key)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
  db.close()
}

async function removeAudioAndMetadata(cacheKey: string): Promise<void> {
  const db = await openDatabase()
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction([AUDIO_STORE, AUDIO_META_STORE], 'readwrite')
    transaction.objectStore(AUDIO_STORE).delete(cacheKey)
    transaction.objectStore(AUDIO_META_STORE).delete(cacheKey)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
  db.close()
}

function isExpired(value: string, nowMs = Date.now()): boolean {
  const expiresAt = Date.parse(value)
  return !Number.isFinite(expiresAt) || expiresAt <= nowMs
}

export const audioStore = {
  put: (cacheKey: string, blob: Blob) => putValue(AUDIO_STORE, cacheKey, blob),

  putShared: async (cacheKey: string, blob: Blob, shareId: string, accessExpiresAt: string) => {
    if (isExpired(accessExpiresAt)) {
      await removeAudioAndMetadata(cacheKey).catch(() => undefined)
      throw new Error('공유 이용기간이 만료되어 음원을 저장하지 않았습니다.')
    }
    const metadata: SharedAudioCacheMetadata = {
      cacheKey,
      shareId: shareId.trim().toUpperCase(),
      accessExpiresAt,
      storedAt: new Date().toISOString(),
      scope: 'SHARED_LINK',
    }
    const db = await openDatabase()
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction([AUDIO_STORE, AUDIO_META_STORE], 'readwrite')
      transaction.objectStore(AUDIO_STORE).put(blob, cacheKey)
      transaction.objectStore(AUDIO_META_STORE).put(metadata, cacheKey)
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
    })
    db.close()
  },

  get: (cacheKey: string) => getValue<Blob>(AUDIO_STORE, cacheKey),

  getShared: async (cacheKey: string, shareId: string, expectedAccessExpiresAt: string): Promise<Blob | null> => {
    const normalizedShareId = shareId.trim().toUpperCase()
    const metadata = await getValue<SharedAudioCacheMetadata>(AUDIO_META_STORE, cacheKey)
    if (!metadata
      || metadata.scope !== 'SHARED_LINK'
      || metadata.shareId !== normalizedShareId
      || metadata.accessExpiresAt !== expectedAccessExpiresAt
      || isExpired(metadata.accessExpiresAt)) {
      await removeAudioAndMetadata(cacheKey).catch(() => undefined)
      return null
    }
    return getValue<Blob>(AUDIO_STORE, cacheKey)
  },

  pruneExpiredShared: async (): Promise<number> => {
    const metadata = await getAllValues<SharedAudioCacheMetadata>(AUDIO_META_STORE)
    let removed = 0
    for (const item of metadata) {
      if (!item || item.scope !== 'SHARED_LINK' || !item.cacheKey || !item.accessExpiresAt || !isExpired(item.accessExpiresAt)) continue
      await removeAudioAndMetadata(item.cacheKey).catch(() => undefined)
      removed += 1
    }
    return removed
  },

  removeSharedByShareId: async (shareId: string): Promise<number> => {
    const normalizedShareId = shareId.trim().toUpperCase()
    const metadata = await getAllValues<SharedAudioCacheMetadata>(AUDIO_META_STORE)
    let removed = 0
    for (const item of metadata) {
      if (!item || item.scope !== 'SHARED_LINK' || item.shareId !== normalizedShareId || !item.cacheKey) continue
      await removeAudioAndMetadata(item.cacheKey).catch(() => undefined)
      removed += 1
    }
    return removed
  },

  remove: (cacheKey: string) => removeAudioAndMetadata(cacheKey),
}

export const pdfStore = {
  put: (shareId: string, blob: Blob) => putValue(PDF_STORE, shareId, blob),
  get: (shareId: string) => getValue<Blob>(PDF_STORE, shareId),
  remove: (shareId: string) => remove(PDF_STORE, shareId),
}

export const pdfMetadataStore = {
  put: (shareId: string, metadata: PdfMetadata) => putValue(PDF_META_STORE, shareId, metadata),
  get: (shareId: string) => getValue<PdfMetadata>(PDF_META_STORE, shareId),
  remove: (shareId: string) => remove(PDF_META_STORE, shareId),
}

export const pdfBookmarkStore = {
  put: (key: string, pages: PdfBookmarkPages) => putValue(PDF_BOOKMARK_STORE, key, pages),
  get: (key: string) => getValue<PdfBookmarkPages>(PDF_BOOKMARK_STORE, key),
  remove: (key: string) => remove(PDF_BOOKMARK_STORE, key),
}
